/**
 * ADR-0143 §8 (Option A) — Studies list.
 *
 * `/app/studies` — scope-filtered per caller server-side (a viewer sees shared
 * studies read-only; personal/team visibility per the contentScope module).
 * Read-only content surface: studies are born and written through the MCP door;
 * the console is the browsable record.
 */

import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { useViewportFrame } from "../hooks/useViewportFrame";
import { Link } from "react-router-dom";
import { apiFetch } from "../lib/api.js";
import { PageLayout } from "../components/PageLayout.js";
import { LoadingState, ErrorState } from "../components/States.js";
import { ScopePill } from "../components/ScopePill.js";
import { useMyAccountId, type ContentScope } from "../lib/scope.js";
import { USAGE_HELP, usageCountCell, usageTimeCell, type EntryUsage } from "../lib/knowledgeUsage.js";

type StudyListItem = {
  id: string;
  slug: string;
  title: string;
  class: "major" | "child" | "minor";
  /** The measurability axis; null on a study that has not declared one. */
  type: "instrument" | "outcome" | null;
  /** How many things read this study's products. null = the rollup was unavailable. */
  consumer_count: number | null;
  parent: string | null;
  status: "proposed" | "in_progress" | "paused" | "complete" | "superseded";
  scope: ContentScope;
  owner_account_id: string | null;
  owner_display_name: string | null;
  /** Who opened the study. Served by the list endpoint; null when the account is gone. */
  created_by_display_name: string | null;
  updated_at: string;
  /** Door retrieval counters (ADR-0186 Amendment 1); null = usage unavailable (renders as "—"). */
  usage?: EntryUsage | null;
};

function classPill(cls: StudyListItem["class"]): JSX.Element {
  if (cls === "child") return <span className="pill pillNeutral">Child</span>;
  if (cls === "minor") return <span className="pill pillNeutral">Minor</span>;
  return <span className="pill">Major</span>;
}

/**
 * Status is on every row, so it reads as a dot + label rather than a pill:
 * pill weight belongs on the exception (Class, Scope), not on the rule.
 */
function statusText(status: StudyListItem["status"]): JSX.Element {
  switch (status) {
    case "in_progress":
      return <span className="statusDot statusDotOk">In progress</span>;
    case "paused":
      return <span className="statusDot statusDotWarn">Paused</span>;
    case "proposed":
      return <span className="statusDot statusDotMuted">Proposed</span>;
    case "complete":
      return <span className="statusDot">Complete</span>;
    case "superseded":
      return <span className="statusDot statusDotMuted">Superseded</span>;
    default:
      return <span className="statusDot">{status}</span>;
  }
}

export function TenantStudies() {
  const myAccountId = useMyAccountId();
  const [items, setItems] = useState<StudyListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const gridRef = useRef<HTMLDivElement>(null);
  // The grid sits inside a .card: leave room for the card's bottom padding + border on top of the page gutter.
  useViewportFrame(gridRef, { active: !loading && !error, bottomGap: 24 + 16 + 1 });
  // Major-study fold state, default collapsed (the Customers tree idiom).
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const toggleExpanded = (id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiFetch<{ studies: StudyListItem[] }>("/api/tenant/studies");
      setItems(data.studies);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <PageLayout title="Studies">
      <div className="card adminList">
        {error ? <ErrorState detail={error} /> : null}
        {!error && loading ? <LoadingState /> : null}
        {!error && !loading && items.length === 0 ? (
          <div className="adminListEmpty">
            No studies yet. Studies are opened from an Atlas-connected session (create_study) — the console
            is the browsable record.
          </div>
        ) : null}
        {!error && !loading && items.length > 0 ? (() => {
          // Group children under their major. The server orders by status then
          // parent, so a child whose status differs from its major's is NOT
          // adjacent in the payload — regroup here. A child whose parent is not
          // in the list (dangling reference, or the parent is invisible to this
          // caller) stays at top level with its "(child of …)" note.
          const byId = new Set(items.map((s) => s.id));
          const childrenByParent = new Map<string, StudyListItem[]>();
          for (const s of items) {
            if (s.class === "child" && s.parent && byId.has(s.parent)) {
              const arr = childrenByParent.get(s.parent) ?? [];
              arr.push(s);
              childrenByParent.set(s.parent, arr);
            }
          }
          for (const arr of childrenByParent.values()) arr.sort((a, b) => a.id.localeCompare(b.id));
          const topLevel = items.filter((s) => !(s.class === "child" && s.parent && byId.has(s.parent)));

          const renderRow = (s: StudyListItem, depth: number): ReactNode[] => {
            const kids = childrenByParent.get(s.id) ?? [];
            const hasKids = kids.length > 0;
            const isExpanded = expanded.has(s.id);
            const rows: ReactNode[] = [
              <tr key={s.id}>
                <td className="mono" style={{ paddingLeft: 8 + depth * 24, whiteSpace: "nowrap" }}>
                  {hasKids ? (
                    <button
                      type="button"
                      onClick={() => toggleExpanded(s.id)}
                      aria-expanded={isExpanded}
                      aria-label={isExpanded ? "Collapse" : "Expand"}
                      style={{
                        display: "inline-block",
                        width: 18,
                        marginRight: 4,
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                        color: "inherit",
                        padding: 0,
                        fontSize: 12
                      }}
                    >
                      {isExpanded ? "▼" : "▶"}
                    </button>
                  ) : (
                    <span style={{ display: "inline-block", width: 18, marginRight: 4 }} />
                  )}
                  {s.id}
                </td>
                <td>
                  <Link className="adminListName" to={`/app/studies/${encodeURIComponent(s.slug)}`}>
                    {s.title}
                  </Link>
                  {hasKids ? (
                    <span className="muted" style={{ marginLeft: 6 }}>
                      ({kids.length})
                    </span>
                  ) : null}
                  {depth === 0 && s.parent ? <span className="muted"> (child of {s.parent})</span> : null}
                </td>
                <td>{classPill(s.class)}</td>
                <td>{s.type ?? <span className="muted">—</span>}</td>
                <td>{s.consumer_count === null ? <span className="muted">—</span> : s.consumer_count}</td>
                {usageCountCell(s.usage, s.usage?.served_in_search)}
                {usageCountCell(s.usage, s.usage?.read_count)}
                {usageTimeCell(s.usage, s.usage?.last_served_at)}
                {usageTimeCell(s.usage, s.usage?.last_read_at)}
                <td>{s.created_by_display_name ?? <span className="muted">—</span>}</td>
                <td>
                  <ScopePill scope={s.scope} ownerDisplayName={s.owner_display_name} ownerIsViewer={s.owner_account_id === myAccountId} />
                </td>
                <td>{statusText(s.status)}</td>
                <td>{new Date(s.updated_at).toLocaleString()}</td>
              </tr>
            ];
            if (hasKids && isExpanded) {
              for (const kid of kids) rows.push(...renderRow(kid, depth + 1));
            }
            return rows;
          };

          return (
            <>
              {childrenByParent.size > 0 ? (
                <div style={{ display: "flex", gap: 12, marginBottom: 6, fontSize: 13 }}>
                  <button
                    type="button"
                    className="muted"
                    style={{ background: "none", border: "none", padding: 0, cursor: "pointer", textDecoration: "underline" }}
                    onClick={() => setExpanded(new Set(childrenByParent.keys()))}
                  >
                    Expand all
                  </button>
                  <button
                    type="button"
                    className="muted"
                    style={{ background: "none", border: "none", padding: 0, cursor: "pointer", textDecoration: "underline" }}
                    onClick={() => setExpanded(new Set())}
                  >
                    Collapse all
                  </button>
                </div>
              ) : null}
              <div className="muted" style={{ fontSize: 12 }}>{USAGE_HELP.caption}</div>
              <div className="tableWrap tableWrapFrame" ref={gridRef}>
                <table className="table">
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Title</th>
                      <th>Class</th>
                      <th>Type</th>
                      <th>Consumers</th>
                      <th title={USAGE_HELP.searchHits}>Search hits</th>
                      <th title={USAGE_HELP.reads}>Reads</th>
                      <th title={USAGE_HELP.lastHit}>Last hit</th>
                      <th title={USAGE_HELP.lastRead}>Last read</th>
                      <th>Created by</th>
                      <th>Scope</th>
                      <th>Status</th>
                      <th>Updated</th>
                    </tr>
                  </thead>
                  <tbody>{topLevel.map((s) => renderRow(s, 0))}</tbody>
                </table>
              </div>
            </>
          );
        })() : null}
      </div>
    </PageLayout>
  );
}
